import { Box } from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card';
import { activityService } from 'features/cost-analysis/team/activities/api/activity.service';
import { useMemo, useRef, useState } from 'react';
import AddActivityDialog from './AddActivityDialog';
import { ExpertiseChart } from './ExpertiseChart';
import { AddButton } from 'components/ui';
import EnhancedDataGrid from 'components/enhanced/data-grid/EnhancedDataGrid';
import { ActivitiesColumns } from '../../activities/activities';

const BACKGROUND_COLORS = [
  'rgba(255, 99, 132, 0.2)',
  'rgba(54, 162, 235, 0.2)',
  'rgba(255, 206, 86, 0.2)',
  'rgba(75, 192, 192, 0.2)',
  'rgba(153, 102, 255, 0.2)',
  'rgba(255, 159, 64, 0.2)'
];

export default function ActivitiesTab({ expertiseId, expertise, activities, setActivities }) {
  const [openActiviryModal, setOpenActiviryModal] = useState<boolean>(false);
  const refreshGrid = useRef();

  const columns = ActivitiesColumns();

  const handleGridRowsChange = (gridInstance: any) => {
    const currentRows = gridInstance?.results || gridInstance;
    if (Array.isArray(currentRows)) {
      if (currentRows.length !== activities.length || currentRows[0]?.id !== activities[0]?.id) {
        setActivities(currentRows);
      }
    }
  };

  const handleGetMethod = async (
    pageSize: number,
    offset: number,
    params: Record<string, string>
  ) => {
    return await activityService.getAllPaginated(pageSize, offset, {
      ...params,
      expertise_id: String(expertiseId)
    });
  };

  const graphData = useMemo(() => {
    if (!activities || activities.length === 0) return null;

    const labels: string[] = [];
    const hoursData: number[] = [];
    const percentagesData: number[] = [];
    const costsData: number[] = [];

    const baseHours = expertise?.monthly_hours;
    const baseCost = expertise?.monthly_cost;

    activities.forEach((activity) => {
      if (!activity) return;

      labels.push(activity.name || '');

      const hours = activity.monthly_hours || (baseHours * activity.monthly_percentage) / 100;
      const percentage = activity.monthly_percentage || (hours / baseHours) * 100;
      const cost = baseCost * (activity.monthly_percentage / 100 || hours / baseHours);

      hoursData.push(hours);
      percentagesData.push(percentage);
      costsData.push(cost);
    });

    return {
      labels,
      datasets: [
        { label: 'Hours [h]', data: hoursData, backgroundColor: BACKGROUND_COLORS, borderWidth: 1 },
        {
          label: 'Percentage [%]',
          data: percentagesData,
          backgroundColor: BACKGROUND_COLORS,
          borderWidth: 1
        },
        { label: 'Cost [€]', data: costsData, backgroundColor: BACKGROUND_COLORS, borderWidth: 1 }
      ]
    };
  }, [activities, expertise]);

  return (
    <>
      <Box className="pt-4 flex flex-col gap-6">
        <EnhancedCard
          title={'Elenco Attività Operative'}
          subtitle={
            'Gestisci e monitora i task associati a questa competenza, definendo ore, obiettivi di output e centri di costo relativi'
          }
          buttons={
            <AddButton
              onClick={() => setOpenActiviryModal(true)}
              prefix={'pages.expertises.view'}
            />
          }>
          <EnhancedDataGrid
            columns={columns}
            service={activityService}
            refresh={refreshGrid}
            rows={handleGridRowsChange}
            outerParams={{ expertise_id: String(expertise.id) }}
            getMethod={handleGetMethod}
          />
        </EnhancedCard>
        {graphData && (
          <EnhancedCard
            title="Grafico dati"
            subtitle={`Visualizzazione grafica dell'allocazione oraria, percentuale e dei costi mensili stimati per le attività correnti.`}>
            <Box className="mt-3 p-2 max-w-xl mx-auto w-full">
              <ExpertiseChart graphData={graphData} />
            </Box>
          </EnhancedCard>
        )}
      </Box>
      <AddActivityDialog
        open={openActiviryModal}
        setOpen={setOpenActiviryModal}
        callback={refreshGrid.current}
        expertiseId={+expertiseId}
      />
    </>
  );
}
